import { type Action } from '@canvas/model/permissions';

import { canAddCommentPolicy } from './policy-definitions/persistent/can-add-comment';
import { canDeleteCommentPolicy } from './policy-definitions/persistent/can-delete-comment';
import { canExport } from './policy-definitions/persistent/can-export';
import { canReadPolicy } from './policy-definitions/persistent/can-read';
import { canWritePolicy } from './policy-definitions/persistent/can-write';
import { isAllowance, isDisallowance, type Policy } from './policy-manager-types';

const defaultPolicies = [
  canWritePolicy,
  canReadPolicy,
  canAddCommentPolicy,
  canDeleteCommentPolicy,
  canExport,
];

export class PolicyManager {
  #policies: Policy[] = [...defaultPolicies];
  #subscribers: VoidFunction[] = [];

  addPolicies = (tag: string, policies: Policy[]) => {
    for (const policy of policies) {
      this.addPolicy(tag, policy);
    }

    this.#subscribers.forEach((subscriber) => subscriber());
  };

  removePolicies = (tag: string) => {
    for (const policy of this.#policies) {
      this.removePolicy(tag, policy.id);
    }

    this.#subscribers.forEach((subscriber) => subscriber());
  };

  summarise = () => {
    const active: string[] = [];

    for (const policy of this.#policies) {
      active.push(policy.id);
    }

    return active.join('|');
  };

  can = (action: Action, state: Set<Action>) => {
    const outcomes = this.#policies.map((p) => p.rule(state));

    const allowance = outcomes.find(
      (outcome) => isAllowance(outcome) && outcome.can === action
    );
    const disallowance = outcomes.find(
      (outcome) => isDisallowance(outcome) && outcome.cannot === action
    );

    // always favour disallowances - they CANNOT be overriden
    if (disallowance) {
      return false;
    }

    // only allow an action if it is explictly not disallowed
    if (allowance && !disallowance) {
      return true;
    }

    return false;
  };

  cannot = (action: Action, state: Set<Action>) => {
    return !this.can(action, state);
  };

  subscribe(cb: VoidFunction) {
    this.#subscribers.push(cb);

    return () => this.unsubscribe(cb);
  }

  unsubscribe(cb: VoidFunction) {
    const idx = this.#subscribers.findIndex((s) => s === cb);
    if (idx === -1) {
      return;
    }

    this.#subscribers.splice(idx, 1);
  }

  destroy() {
    this.#policies = [...defaultPolicies];
    this.#subscribers.length = 0;
  }

  private addPolicy = (tag: string, policy: Policy) => {
    this.#policies.push({ ...policy, tag });
  };

  private removePolicy = (tag: string, id: string) => {
    const idx = this.#policies.findIndex((p) => p.id === id && p.tag === tag);
    if (idx === -1) {
      return;
    }

    const policy = this.#policies[idx];
    if (policy.type === 'persistent') {
      return;
    }

    this.#policies.splice(idx, 1);
  };
}
